import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CustomAvatarComponent } from "../../../../../shared/components/custom-avatar/custom-avatar.component";
import { CustomButtonComponent } from '../../../../../shared/components/custom-button/custom-button.component';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CustomDropdownComponent } from "../../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { ToggleButtonComponent } from '../../../../../shared/components/toggle-button/toggle-button.component';
import { UserInitialsService } from '../../../../../core/services/user-initials.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NavigationShareService } from '../../../../../core/services/navigation-share.service';
import { AuthValidatorService } from '../../../../../core/services/validators/auth-validator.service';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, firstValueFrom, lastValueFrom } from 'rxjs';
import { AuthService } from '../../../../../core/api/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { UserDataService } from '../../../../../core/services/user-data.service';
import { UserService } from '../../../../../core/api/user.service';
import { InviteUserResponse, PasswordValidator, UiLanguage, UserSettings } from '../../../../../core/interfaces/user-interfaces';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { CompanyService } from '../../../../../core/api/company.service';
import { RecentUploadsService } from '../../../../../core/services/recent-uploads.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { SalesAgentsService } from '../../../../../core/api/sales-agents.service';
import { environment } from '../../../../../../environments/environment';
import { PaymentService } from '../../../../../core/api/payment.service';
import { PromotionCodeAssignRequest } from '../../../../../core/interfaces/payment-interfaces';

@Component({
  selector: 'app-onboarding-step-one',
  standalone: true,
  imports: [CustomAvatarComponent, CustomButtonComponent, LabeledInputComponent, DropdownModule, FormsModule, CommonModule, CustomDropdownComponent, PrimaryButtonComponent, ToggleButtonComponent, TranslocoPipe, ToastComponent],
  templateUrl: './onboarding-step-one.component.html',
  styleUrl: './onboarding-step-one.component.css'
})
export class OnboardingStepOneComponent implements OnInit {
  // Text Inputs
  firstNameInput: string = '';
  firstNameInputError: string | null = null;

  lastNameInput: string = '';
  lastNameInputError: string | null = null;

  passwordInput: string = '';
  passwordInputError: string | null = null;

  // Dropdown Menu
  rolesOptions: string[] = [];
  selectedRole: number | undefined;
  serverRoleOptions: string[] = [];
  selectRoleShowError: boolean = false;

  // Visual password validator
  validatePasswordStatus: PasswordValidator = {
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  };

  // Toggle Button
  toggleButton: boolean = true;

  termsAccepted: boolean = false;
  termsAcceptedError: boolean = false;

  continueClicked: boolean = false;

  userEmail: string | undefined = undefined;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Image upload
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  imagePreview: string | null = null;
  imageValidationError: boolean = false;

  socialLogin: boolean = false;

  inviteId: string | null = null;
  invitationResponse: InviteUserResponse | undefined = undefined;
  inviteRegister: boolean = false;

  agentId: string | null = null;
  promotionCode: string | undefined = undefined;

  termsAndConditionsURL = environment.termsAndConditions;
  privacyPolicyURL = environment.privacyPolicy

  constructor(private userInitialsService: UserInitialsService, private translocoService: TranslocoService, private shareService: NavigationShareService, private authValidator: AuthValidatorService, private router: Router, private authService: AuthService, private userData: UserDataService, private userService: UserService, private apiError: ApiErrorService, private route: ActivatedRoute, private companyService: CompanyService, private recentUploads: RecentUploadsService, private languageService: LanguageService, private salesAgentsService: SalesAgentsService, private paymentService: PaymentService) { }

  async ngOnInit(): Promise<void> {
    // Check query params
    this.inviteId = this.route.snapshot.queryParams['invite'] ?? null;
    this.agentId = this.route.snapshot.queryParams['agent_invite'] ?? null;
    this.promotionCode = this.route.snapshot.queryParams['promotion'] ?? undefined;

    // Return if data is not shared from previous step or via url in case of invite
    if (this.inviteId !== null) {
      try {
        this.invitationResponse = await firstValueFrom(this.userService.getInviteLinkData(this.inviteId));
      } catch (error) {
        var crid = 'UNKNOWN-CRID';
        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.continueClicked = true;
        setTimeout(() => {
          this.router.navigate(['/auth/signup']);
        }, 8000);
        return;
      }

      // Prefill role
      const serverRoleOptions = ["ADMIN", "MANAGER", "SR_MANAGER", "DIRECTOR"];
      this.selectedRole = serverRoleOptions.indexOf(this.invitationResponse?.assigned_role!) ?? null;

      this.userEmail = this.invitationResponse!.email;
      this.inviteRegister = true;
    }
    else if (this.agentId != null) {
      this.userEmail = this.shareService.getState("email");
      if (!this.userEmail) {
        this.router.navigate(['/auth/signup'], { queryParams: { agent_invite: this.agentId } });
      }
    }
    else {
      this.userEmail = this.shareService.getState("email");
    }

    if (!this.userEmail) {
      this.router.navigate(['/auth/signup']);
    }

    this.socialLogin = this.shareService.getState("socialLogin") ?? false;

    // Load existing data if available
    if (this.socialLogin && this.userData.getUser()) {
      this.firstNameInput = this.userData.getUser()?.first_name ?? '';
      this.lastNameInput = this.userData.getUser()?.last_name ?? '';
      this.imagePreview = this.userData.getUser()?.settings?.image_url ?? null;
    }

    this.serverRoleOptions = this.shareService.getState("roles");
    if (!this.serverRoleOptions) {
      // Populate with hardcoded data for social login
      this.serverRoleOptions = ["ADMIN", "MANAGER", "SR_MANAGER", "DIRECTOR"];
    }
    else {
      this.serverRoleOptions.shift();
    }
    // Roles are currently hardcoded to ensure proper translations
    combineLatest([
      this.translocoService.selectTranslate('shared.roles.admin'),
      this.translocoService.selectTranslate('shared.roles.manager'),
      this.translocoService.selectTranslate('shared.roles.sr_manager'),
      this.translocoService.selectTranslate('shared.roles.director'),
    ]).subscribe(([admin, manager, srManager, director]) => {
      this.rolesOptions = [admin, manager, srManager, director];
    });

  }

  get userInitials(): string {
    return this.userInitialsService.getUserInitials(this.firstNameInput, this.lastNameInput);
  }

  async assignPromotionCode() {
    try {
      var request: PromotionCodeAssignRequest = {
        'auto_promotion_code': this.promotionCode!
      };
      await lastValueFrom(this.paymentService.assignPromotionCodeToUser(request));
    } catch (error) {
      // pass
    }
  }

  async continueButtonClick() {
    if (this.socialLogin) {
      this.continueSocialButtonClick();
      return;
    }
    // Prevent multiple clicks
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    // Trim inputs
    const firstNameInputTrim = this.firstNameInput.trim();
    const lastNameInputTrim = this.lastNameInput.trim();
    const passwordInputTrim = this.passwordInput.trim();

    // Validate inputs
    const firstNameError = this.authValidator.validateFirstName(firstNameInputTrim);
    const lastNameError = this.authValidator.validateLastName(lastNameInputTrim);
    const passwordError = this.authValidator.validatePassword(passwordInputTrim);

    this.firstNameInputError = firstNameError;
    this.lastNameInputError = lastNameError;
    this.passwordInputError = passwordError;

    if (firstNameError || lastNameError || passwordError) {
      this.continueClicked = false;
      return;
    }

    this.selectedRole = this.serverRoleOptions.indexOf(this.invitationResponse?.assigned_role!) ?? null;
    const finalRole = this.selectedRole === undefined ? 'UNDEFINED' : this.serverRoleOptions.at(this.selectedRole!);
    if (finalRole === 'UNDEFINED') {
      this.selectRoleShowError = true;
      return;
    }
    this.selectRoleShowError = false;

    if (!this.termsAccepted) {
      this.termsAcceptedError = true;
      this.continueClicked = false;
      return;
    }
    this.termsAcceptedError = false;

    let registerResponse = undefined;
    try {
      registerResponse = await firstValueFrom(this.authService.register(this.userEmail!, passwordInputTrim, false, false, false, firstNameInputTrim, lastNameInputTrim, finalRole));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        if (error.error?.code === 'SVC-5001') {
          // Go to login if user alreaday exists
          this.router.navigate(['/auth/signup']);
          return;
        }
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }

      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.continueClicked = false;
      return;
    }

    // Login user and get session token
    let loginResult = undefined;
    try {
      loginResult = await firstValueFrom(this.authService.login(this.userEmail!, passwordInputTrim));
      this.recentUploads.clearUploads();
    }
    catch (error) {
      this.router.navigate(['/auth/signup']);
      return;
    }

    // Save subscribe to updates status
    await this.userData.loadUserData();

    let user = this.userData.getUser();
    if (user?.settings) {
      user.settings!.subscribe_to_updates = this.toggleButton;
      user.settings!.workspace_preferences = ["Invited by team member"];
      user.settings.accepted_terms = this.termsAccepted;

      const currentLanguage = this.languageService.getCurrentUILanguage().toUpperCase();
      const finalLanguage = Object.values(UiLanguage).includes(currentLanguage as UiLanguage) ? (currentLanguage as UiLanguage) : UiLanguage.EN;
      user.settings.ui_language = finalLanguage;

      let updateUserSettingResponse = undefined;
      try {
        updateUserSettingResponse = await firstValueFrom(this.userService.updateUserSettings(user.settings.id, user.settings));

        user.settings = updateUserSettingResponse as UserSettings;
        this.userData.setUser(user);
      } catch (error) {
        // pass
      }
    }

    // Upload user image
    if (this.selectedFile) {
      try {
        await lastValueFrom(this.userService.uploadImage(this.selectedFile));
      } catch (error) {
        // pass
      }
    }

    // Check sales agent invite status
    if (this.agentId !== null) {
      try {
        await lastValueFrom(this.salesAgentsService.trackSalesAgent(this.agentId));
      } catch (error) {
        var crid = 'UNKNOWN-CRID';
        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.continueClicked = false;
        return;
      }
    }

    // Check invitation status
    if (this.inviteId !== null) {
      // Add user to company
      let user = this.userData.getUser();
      if (user) {
        let assignUserResponse = undefined;
        try {
          assignUserResponse = await firstValueFrom(this.companyService.addUserToCompany(this.invitationResponse!.assigned_to_company.id));

          user.company = assignUserResponse.company;
          this.userData.setUser(user);
        } catch (error) {
          var crid = 'UNKNOWN-CRID';
          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.continueClicked = false;
          return;
        }

        this.router.navigate(["/dashboard"]);
        this.continueClicked = false;
        return;
      }
    }

    // Assign auto promotion code
    if (this.promotionCode) {
      await this.assignPromotionCode();
    }

    this.router.navigate(["/auth/onboarding/"], { queryParams: { step: 2 } });
    this.continueClicked = false;
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  validateImage(file: File) {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg'];

    if (!allowedTypes.includes(file.type)) {
      this.imageValidationError = true;
      return;
    }

    // Validate image size
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width < 256 || img.height < 256) {
        this.imageValidationError = true;
        return;
      }

      this.selectedFile = file;
      this.imagePreview = img.src;
      this.imageValidationError = false;
    };
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (file) {
      this.validateImage(file);
    }
  }

  async continueSocialButtonClick() {
    // Prevent multiple clicks
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    // Trim inputs
    const firstNameInputTrim = this.firstNameInput.trim();
    const lastNameInputTrim = this.lastNameInput.trim();

    // Validate inputs
    const firstNameError = this.authValidator.validateFirstName(firstNameInputTrim);
    const lastNameError = this.authValidator.validateLastName(lastNameInputTrim);

    this.firstNameInputError = firstNameError;
    this.lastNameInputError = lastNameError;

    if (firstNameError || lastNameError) {
      this.continueClicked = false;
      return;
    }

    const finalRole = this.selectedRole === undefined ? 'UNDEFINED' : this.serverRoleOptions.at(this.selectedRole!);
    if (finalRole === 'UNDEFINED') {
      this.selectRoleShowError = true;
      this.continueClicked = false;
      return;
    }
    this.selectRoleShowError = false;

    if (!this.termsAccepted) {
      this.termsAcceptedError = true;
      this.continueClicked = false;
      return;
    }
    this.termsAcceptedError = false;

    // Save subscribe to updates status
    await this.userData.loadUserData();

    let user = this.userData.getUser();
    if (user?.settings) {
      user.settings!.subscribe_to_updates = this.toggleButton;
      user.settings.accepted_terms = this.termsAccepted;

      let updateUserSettingResponse = undefined;
      try {
        updateUserSettingResponse = await firstValueFrom(this.userService.updateUserSettings(user.settings.id, user.settings));

        user.settings = updateUserSettingResponse as UserSettings;
        this.userData.setUser(user);
      } catch (error) {
        // pass
      }
    }

    user = this.userData.getUser();
    if (user) {
      user.first_name = firstNameInputTrim;
      user.last_name = lastNameInputTrim;
      user.role = finalRole!;

      let updateUserResponse = undefined;
      try {
        updateUserResponse = await firstValueFrom(this.userService.updateUser(user));

        this.userData.setUser(user);
      } catch (error) {
        // pass
      }
    }

    // Upload user image
    if (this.selectedFile) {
      try {
        await lastValueFrom(this.userService.uploadImage(this.selectedFile));
      } catch (error) {
        // pass
      }
    }

    // Assign auto promotion code
    if (this.promotionCode) {
      await this.assignPromotionCode();
    }

    this.router.navigate(["/auth/onboarding/"], { queryParams: { step: 2 } });
    this.continueClicked = false;
  }

  validatePasswordVisualy() {
    const passwordInputTrim = this.passwordInput.trim();
    this.validatePasswordStatus = this.authValidator.validatePasswordVisualy(passwordInputTrim);
  }
}
